import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from "react";

import {
  clearAccessToken,
  exchangeToken,
  fetchSelf,
  refreshAccessToken,
  revokeToken,
  setAccessToken,
  type SessionUser,
} from "@/lib/api";

// Single state machine for the analytics session.
//
// Firebase is an identity provider only — used at sign-in to mint one ID
// token that the login page hands to `signIn(idToken)`. The moment the
// backend exchange succeeds, we Firebase-signOut so `auth.currentUser`
// never persists beyond the few hundred ms it takes to mint the token.
// After that, the session lives entirely in the httpOnly refresh cookie
// plus in-memory access token. We never subscribe to onAuthStateChanged
// and never read auth.currentUser.

type Status =
  | "bootstrapping"
  | "unauthenticated"
  | "loadingProfile"
  | "forbidden"
  | "authenticated";

type State = {
  status: Status;
  user: SessionUser | null;
  error: string | null;
};

type Action =
  | { type: "loadingProfile" }
  | { type: "profileLoaded"; user: SessionUser }
  | { type: "profileRefreshed"; user: SessionUser }
  | { type: "forbidden"; reason: string }
  | { type: "sessionEnded"; reason?: string }
  | { type: "clearError" };

const INITIAL: State = { status: "bootstrapping", user: null, error: null };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "loadingProfile": {
      if (state.status === "authenticated" && state.user) {
        return state;
      }
      return { status: "loadingProfile", user: null, error: null };
    }
    case "profileLoaded": {
      if (state.status === "unauthenticated" || state.status === "forbidden") {
        return state;
      }
      return { status: "authenticated", user: action.user, error: null };
    }
    case "profileRefreshed": {
      if (!state.user) {
        return state;
      }
      return { ...state, user: action.user };
    }
    case "forbidden": {
      return { status: "forbidden", user: null, error: action.reason };
    }
    case "sessionEnded": {
      return {
        status: "unauthenticated",
        user: null,
        error: action.reason ?? state.error,
      };
    }
    case "clearError": {
      if (!state.error) {
        return state;
      }
      return { ...state, error: null };
    }
    default: {
      return state;
    }
  }
}

type SessionValue = State & {
  signIn: (firebaseIdToken: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  refreshUser: () => Promise<SessionUser | null>;
};

const SessionContext = createContext<SessionValue | null>(null);

const REFRESH_LEAD_MS = 30_000;
const MIN_REFRESH_MS = 10_000;

function isAdmin(user: SessionUser | null | undefined): boolean {
  return user?.type === "ADMIN";
}

function flushFirebaseInBackground(): void {
  void import("@/lib/firebase-auth")
    .then(({ logOut }) => logOut())
    .catch(() => undefined);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generationRef = useRef(0);
  // Ref-indirect recursion: scheduleRefresh re-arms itself after each refresh,
  // but referencing it inside its own useCallback body is a stale-closure trap.
  // The ref always points at the latest function.
  const scheduleRefreshRef = useRef<(expiresInSeconds: number) => void>(() => undefined);

  const cancelRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const scheduleRefresh = useCallback(
    (expiresInSeconds: number) => {
      cancelRefresh();
      const delay = Math.max(expiresInSeconds * 1000 - REFRESH_LEAD_MS, MIN_REFRESH_MS);
      const myGen = generationRef.current;
      refreshTimerRef.current = setTimeout(async () => {
        try {
          const next = await refreshAccessToken();
          if (generationRef.current !== myGen) {
            return;
          }
          setAccessToken(next.accessToken);
          scheduleRefreshRef.current(next.expiresIn);
        } catch {
          if (generationRef.current !== myGen) {
            return;
          }
          clearAccessToken();
          dispatch({
            type: "sessionEnded",
            reason: "Your session has expired. Please sign in again.",
          });
        }
      }, delay);
    },
    [cancelRefresh],
  );
  useLayoutEffect(() => {
    scheduleRefreshRef.current = scheduleRefresh;
  }, [scheduleRefresh]);

  const finalizeSession = useCallback(
    async (myGen: number) => {
      const data = await fetchSelf();
      if (generationRef.current !== myGen) {
        return;
      }
      if (!data) {
        cancelRefresh();
        clearAccessToken();
        await revokeToken().catch(() => undefined);
        dispatch({ type: "forbidden", reason: "No account found for this user." });
        return;
      }
      if (!isAdmin(data)) {
        cancelRefresh();
        clearAccessToken();
        await revokeToken().catch(() => undefined);
        dispatch({
          type: "forbidden",
          reason: "This portal is for administrators only.",
        });
        return;
      }
      dispatch({ type: "profileLoaded", user: data });
    },
    [cancelRefresh],
  );

  useEffect(() => {
    const myGen = generationRef.current;
    (async () => {
      try {
        const next = await refreshAccessToken();
        if (generationRef.current !== myGen) {
          return;
        }
        setAccessToken(next.accessToken);
        scheduleRefresh(next.expiresIn);
        dispatch({ type: "loadingProfile" });
        await finalizeSession(myGen);
      } catch {
        if (generationRef.current !== myGen) {
          return;
        }
        clearAccessToken();
        dispatch({ type: "sessionEnded" });
      }
    })();

    return () => {
      generationRef.current += 1;
      cancelRefresh();
    };
  }, [cancelRefresh, finalizeSession, scheduleRefresh]);

  const signIn = useCallback(
    async (firebaseIdToken: string) => {
      generationRef.current += 1;
      const myGen = generationRef.current;
      cancelRefresh();
      dispatch({ type: "loadingProfile" });
      try {
        const exchange = await exchangeToken(firebaseIdToken);
        if (generationRef.current !== myGen) {
          return;
        }
        setAccessToken(exchange.accessToken);
        scheduleRefresh(exchange.expiresIn);
        flushFirebaseInBackground();
        await finalizeSession(myGen);
      } catch (err) {
        if (generationRef.current !== myGen) {
          return;
        }
        cancelRefresh();
        clearAccessToken();
        flushFirebaseInBackground();
        const reason = err instanceof Error ? err.message : "Sign-in failed.";
        dispatch({ type: "sessionEnded", reason });
        throw err;
      }
    },
    [cancelRefresh, finalizeSession, scheduleRefresh],
  );

  const signOutFn = useCallback(async () => {
    generationRef.current += 1;
    cancelRefresh();
    await revokeToken().catch(() => undefined);
    clearAccessToken();
    dispatch({ type: "sessionEnded" });
  }, [cancelRefresh]);

  const clearError = useCallback(() => {
    dispatch({ type: "clearError" });
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await fetchSelf();
      if (!data) {
        return null;
      }
      dispatch({ type: "profileRefreshed", user: data });
      return data;
    } catch {
      return null;
    }
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      ...state,
      signIn,
      signOut: signOutFn,
      clearError,
      refreshUser,
    }),
    [state, signIn, signOutFn, clearError, refreshUser],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error("useSession must be used within SessionProvider");
  }
  return ctx;
}
